/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import CircleIcon from '../icons/generated/custom/Circle';
import RhombusIcon from '../icons/generated/custom/Rhombus';
import SquareIcon from '../icons/generated/custom/Square';
import TriangleIcon from '../icons/generated/custom/Triangle';

/**
 *
 *
 * Types
 *
 *
 */

type EventTypeStatusLabelProps = {
  /** Additional Classnames */
  className?: string;

  /** The content displayed inside the status label */
  children: React.ReactNode;

  /** Color of the status label that indicates its purpose. */
  variant?: (typeof StatusLabelVariants)[number];

  /** Fill type of status label, determines the background and border styles. */
  fill?: (typeof StatusLabelFill)[number];

  /** Whether the status label have an icon */
  hasIcon?: boolean;

  /** Size of the status label */
  size?: 'small' | 'large';
};

const StatusLabelVariants = [
  'info',
  'success',
  'warning',
  'danger',
  'default',
  'discovery',
] as const;

const StatusLabelFill = ['filled', 'outlined', 'semi-filled', 'clean'] as const;

export const StatusLabel = <T extends React.ElementType = 'span'>({
  as,
  variant = 'default',
  children,
  className,
  style,
  fill = 'filled',
  hasIcon = false,
  htmlAttributes,
  size = 'large',
  ref,
  ...restProps
}: PolymorphicCommonProps<T, EventTypeStatusLabelProps>) => {
  let baseClasses: string;
  let icon: React.ReactNode | null;

  // Clean Status label without icon is not allowed
  if (!hasIcon && fill === 'clean') {
    console.error('Clean StatusLabel has to have an icon');
  }

  // All colors map to the light-theme palette except the info
  const mappedColor = variant === 'info' ? 'primary' : variant;

  if (hasIcon) {
    const isFilled = fill === 'filled';
    if (mappedColor === 'default') {
      const fillColor = isFilled
        ? 'currentColor'
        : 'var(--theme-color-neutral-bg-status)';
      icon = <SquareIcon htmlAttributes={{ color: fillColor }} />;
    } else if (mappedColor === 'primary') {
      const fillColor = isFilled
        ? 'currentColor'
        : 'var(--theme-color-primary-bg-status)';
      icon = <CircleIcon htmlAttributes={{ color: fillColor }} />;
    } else if (mappedColor === 'success') {
      const fillColor = isFilled
        ? 'currentColor'
        : 'var(--theme-color-success-bg-status)';
      icon = <CircleIcon htmlAttributes={{ color: fillColor }} />;
    } else if (mappedColor === 'danger') {
      const fillColor = isFilled
        ? 'currentColor'
        : 'var(--theme-color-danger-bg-status)';
      icon = <RhombusIcon htmlAttributes={{ color: fillColor }} />;
    } else if (mappedColor === 'warning') {
      const fillColor = isFilled
        ? 'currentColor'
        : 'var(--theme-color-warning-bg-status)';
      icon = <TriangleIcon htmlAttributes={{ color: fillColor }} />;
    } else if (mappedColor === 'discovery') {
      const fillColor = isFilled
        ? 'currentColor'
        : 'var(--theme-color-discovery-bg-status)';
      icon = <CircleIcon htmlAttributes={{ color: fillColor }} />;
    }
  }

  if (mappedColor === 'default') {
    baseClasses = classNames({
      [`n-bg-neutral-bg-default n-text-neutral-text-weak n-border-neutral-border-strong`]:
        fill === 'semi-filled',
      [`n-bg-neutral-bg-strongest n-text-neutral-text-inverse`]:
        fill === 'filled',
      [`n-text-neutral-text-weak n-border-neutral-border-strong`]:
        fill === 'outlined',
      [`n-text-neutral-text-weaker`]: fill === 'clean',
    });
  } else {
    baseClasses = classNames({
      [`n-bg-${mappedColor}-bg-strong n-text-neutral-text-inverse`]:
        fill === 'filled',
      [`n-text-${mappedColor}-text n-border-${mappedColor}-border-strong`]:
        fill === 'outlined',
      [`n-bg-${mappedColor}-bg-weak n-text-${mappedColor}-text n-border-${mappedColor}-border-weak`]:
        fill === 'semi-filled',
      [`n-text-neutral-text-default`]: fill === 'clean',
    });
  }

  const classes = classNames('ndl-status-label', baseClasses, className, {
    'ndl-clean': fill === 'clean',
    'ndl-filled': fill === 'filled',
    'ndl-large': size === 'large',
    'ndl-outlined': fill === 'outlined',
    'ndl-semi-filled': fill === 'semi-filled',
    'ndl-small': size === 'small',
  });

  const Component: React.ElementType = as ?? 'span';

  return (
    <Component
      role="status"
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-status-label-content">
        {icon}
        <span
          // To enable native tooltip on hover if the children is a string
          title={typeof children === 'string' ? children : 'status label'}
          className="ndl-status-label-text"
        >
          {children}
        </span>
      </div>
    </Component>
  );
};
